import { css } from '@emotion/css';

import boxShadowMixin from '@editor2/styles/mixins/boxShadow';
import { colors } from '@editor2/styles/colors';
import { pageCommon } from '@editor2/styles/page';

export const tocPage = css`
	${pageCommon}

	.addPageButton {
		font-size: 14px;
		margin-top: 7px;
		margin-left: 30px;
		border-color: ${colors.border};
		background-color: inherit;
		color: ${colors.mainText};
		${boxShadowMixin(1)};

		svg {
			margin-right: 10px;
		}

		:hover {
			background-color: ${colors.background};
		}
	}

	.addPageGroupButton {
		font-size: 14px;
		margin-top: 15px;
		border-color: ${colors.border};
		background-color: inherit;
		color: ${colors.mainText};
		${boxShadowMixin(1)};

		svg {
			margin-right: 10px;
		}

		:hover {
			background-color: ${colors.background};
		}
	}

	.addChapterButton {
		font-size: 14px;
		margin-top: 30px;
		border-color: ${colors.border};
		background-color: inherit;
		color: ${colors.mainText};
		${boxShadowMixin(1)};
		width: fit-content;

		svg {
			margin-right: 10px;
		}

		:hover {
			background-color: ${colors.background};
		}
	}

	.listHidden {
		display: none;
	}

	.listShown {
		display: block;
	}

	.collapseIcon {
		margin-left: 10px;
		transition-duration: 500ms;
	}

	.collapseIconRotate {
		margin-left: 10px;
		transform: rotate(-90deg);
		transition-duration: 500ms;
	}

	.CodeMirror {
		border-radius: 4px;
		height: 100%;
		font-size: 12px;
	}

	.tocTabs {
		a {
			color: ${colors.mainText};
			border-radius: 0;
		}

		.nav-link.disabled {
			background-color: ${colors.background};
		}
	}

	.nav-tabs .nav-link.active {
		border-top-color: white;
	}
`;

export const tocEditorContainer = css`
	padding: 30px 40px;
	font-size: 14px;

	.courseName {
		font-weight: 600;
		margin-bottom: 30px;
	}

	.yamlConfig {
		margin-bottom: 10px;
	}
`;

export const tocTools = css`
	padding: 30px 40px;
	display: flex;
	justify-content: space-evenly;

	.toolsButton {
		padding: 40px 20px;
		${boxShadowMixin(1)};

		svg {
			margin-right: 10px;
		}

		:hover {
			background-color: ${colors.background};
		}
	}
`;

export const tocBlock = css`
	padding: 30px 40px;
	display: flex;
	flex-direction: column;
	flex: 1;
	color: ${colors.mainText};

	.col {
		padding-right: 0;
		padding-left: 0;

		:not(:first-child) {
			padding-left: 35px;
		}
	}
`;

export const yamlEditor = css`
	padding: 30px 40px;

	.codeBlock {
		border: 1px solid ${colors.border};
		border-radius: 4px;
	}
`;

export const tocFiles = css`
	padding: 30px 40px;
	font-size: 14px;

	.filesHeader {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 20px;
		gap: 16px;
	}

	.filesHeading {
		font-size: 20px;
		font-weight: 700;
		margin: 0;
		color: ${colors.mainText};
	}

	.availabilityToggle {
		text-align: right;
		flex-shrink: 1;
		max-width: 420px;

		.custom-switch {
			display: flex;
			align-items: center;
			justify-content: flex-end;
		}
	}

	.availabilityNote {
		margin: 4px 0 0 auto;
		font-size: 12px;
		color: ${colors.infoText};
		max-width: 300px;
	}

	.filesTable {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0;
		table-layout: fixed;
		border: 1px solid ${colors.border};
		border-radius: 8px;
		overflow: hidden;

		thead th {
			font-weight: 600;
			padding: 8px 12px;
			border-bottom: 2px solid ${colors.border};
			color: ${colors.mainText};
			background-color: ${colors.background};

			&.dragHandleHeader {
				width: 28px;
			}
			&:nth-child(2) {
				width: 32%;
			}
			&:nth-child(3) {
				width: 42%;
			}
			&:nth-child(4) {
				width: 12%;
			}
			&:nth-child(5) {
				width: 6%;
			}
		}

		tbody td {
			padding: 8px 12px;
			vertical-align: middle;
			color: ${colors.mainText};
			border-bottom: 1px solid ${colors.border};
		}

		tbody tr:hover {
			background-color: ${colors.background};
		}

		.selfServeSampleCell {
			text-align: center;
		}

		.actionsCell {
			text-align: center;
		}

		.emptyState {
			text-align: center;
			padding: 24px;
			color: ${colors.infoText};
		}
	}

	.fileActionsToggle {
		background: none;
		border: none;
		padding: 2px 4px;
		cursor: pointer;
		color: ${colors.mainText};
		line-height: 1;

		&::after {
			display: none;
		}

		&:hover {
			color: ${colors.hoverText};
		}
	}

	.dragHandleCell {
		width: 28px;
		text-align: center;
		cursor: grab;
		color: ${colors.infoText};
		vertical-align: middle;

		&:hover {
			color: ${colors.mainText};
		}
	}

	.fileMainRow.isDragging {
		background-color: ${colors.background};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
		display: table;
		table-layout: fixed;
		width: 100%;
	}

	.dangerItem {
		color: #dc3545;
	}

	.uploadArea {
		margin-top: 24px;

		.dropZone {
			border: 2px dashed ${colors.border};
			border-radius: 12px;
			padding: 48px 16px;
			text-align: center;
			cursor: pointer;
			outline: none;
			transition: border-color 0.15s, background-color 0.15s;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 4px;

			&.isDragOver {
				border-color: ${colors.mainText};
				background-color: ${colors.background};
			}

			&:focus-visible {
				box-shadow: 0 0 0 2px ${colors.placeholder};
			}
		}

		.uploadIcon {
			font-size: 32px;
			color: #8e9adf;
			margin-bottom: 8px;
			pointer-events: none;
		}

		.uploadHintPrimary {
			margin: 0;
			color: ${colors.mainText};
			font-size: 15px;
			font-weight: 500;
			pointer-events: none;
		}

		.uploadHintSecondary {
			margin: 0;
			color: ${colors.infoText};
			font-size: 13px;
			pointer-events: none;
		}

		.uploadProgress {
			margin-top: 8px;
			display: flex;
			flex-direction: column;
			gap: 6px;
		}

		.progressBar {
			height: 6px;
			background-color: ${colors.border};
			border-radius: 3px;
			overflow: hidden;
		}

		.progressFill {
			height: 100%;
			background-color: #0d6efd;
			border-radius: 3px;
			transition: width 0.1s linear;
		}

		.uploadError {
			margin: 8px 0 0;
			color: #dc3545;
			font-size: 13px;
		}
	}

	.deckLink {
		margin-top: 8px;
		margin-bottom: 1em;
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 12px;
		color: ${colors.infoText};

		code {
			font-size: 11px;
			color: inherit;
		}
	}

	.copyLinkButton {
		font-size: 11px;
		padding: 1px 6px;
		border: 1px solid ${colors.border};
		border-radius: 3px;
		background: none;
		cursor: pointer;
		color: ${colors.mainText};

		&:hover {
			background-color: ${colors.background};
		}

		&.copied {
			color: #198754;
			border-color: #198754;
		}
	}

	.expandToggle {
		background: none;
		border: none;
		padding: 2px 6px 2px 0;
		cursor: pointer;
		color: ${colors.mainText};
		line-height: 1;
		vertical-align: middle;

		&:hover {
			color: ${colors.hoverText};
		}
	}

	.fileNameContent {
		display: flex;
		align-items: center;
		gap: 2px;
		min-width: 0;
	}

	.chapterPageCell .form-control,
	.responsiveChapterPage .form-control {
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		padding-right: 24px;
	}

	.responsiveChapterPage {
		display: none;
	}

	.responsiveSubHeader {
		display: none;
	}

	@media (max-width: 768px) {
		.filesTable thead th.chapterPageHeader {
			display: none;
		}

		.filesTable tbody td.chapterPageCell {
			display: none;
		}

		.filesTable thead th:nth-child(2) {
			width: 55%;
		}

		.filesTable thead th:nth-child(4) {
			width: 20%;
		}

		.responsiveChapterPage {
			display: block;
			margin-top: 4px;
		}

		.responsiveSubHeader {
			display: inline;
			font-weight: 400;
			font-size: 12px;
			color: ${colors.infoText};
		}
	}

	.fileNameToggle {
		background: none;
		border: none;
		padding: 0;
		cursor: pointer;
		color: inherit;
		font-size: inherit;
		text-align: left;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		max-width: 100%;

		&:hover {
			text-decoration: underline;
		}
	}

	.fileMainRow td {
		border-bottom: none;
	}

	.fileMainRow:not(.isExpanded) td {
		border-bottom: 1px solid ${colors.border};
	}

	.fileDetailRow td {
		padding: 0 12px 12px 36px;
		border-bottom: 1px solid ${colors.border};
	}

	.uploadDetails {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.uploadProgressText {
		font-size: 12px;
		color: #0d6efd;
	}

	.uploadErrorText {
		font-size: 12px;
		color: #dc3545;
	}

	.fileDetails {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	.fileMetadata {
		display: flex;
		flex-wrap: wrap;
		gap: 20px;
		font-size: 13px;
		color: ${colors.infoText};
	}

	.metaItem {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.metaIcon {
		font-size: 14px;
	}

	.fileNotesInput {
		font-size: 13px;
		resize: vertical;
	}

	.statusBadge {
		font-size: 11px;
		font-weight: 500;
		margin-left: 6px;
	}

	.statusPending {
		color: ${colors.infoText};
	}

	.statusUploading {
		color: #0d6efd;
	}

	.statusError {
		color: #dc3545;
	}

	.rowProgressBar {
		height: 4px;
		background: ${colors.border};
		border-radius: 2px;
		margin-top: 4px;
		overflow: hidden;
	}

	.rowProgressFill {
		height: 100%;
		background: #0d6efd;
		border-radius: 2px;
		transition: width 0.1s linear;
	}

	.saveRow {
		margin-top: 16px;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 8px;
	}

	.saveButton {
		min-width: 120px;
	}

	.saveError {
		color: #dc3545;
		font-size: 13px;
	}
`;

export const collapseTools = css`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	margin-bottom: 20px;

	button {
		margin-left: 15px;
		font-size: 14px;
		border-color: ${colors.border};

		:hover {
			color: ${colors.mainText};
			border-color: ${colors.border};
			background-color: ${colors.background};
		}

		svg {
			margin-right: 5px;
		}
	}
`;

export const columnContainer = css`
	padding-right: 0;
	padding-left: 35px;
`;

export const chapterContainer = css`
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	position: relative;

	.chapterHeader {
		display: flex;
		align-items: center;
		justify-content: space-between;
		font-size: 1em;
		font-weight: 600;
		line-height: 1.2;
		margin-bottom: 15px;
		display: flex;
		cursor: grab;
		background-color: ${colors.background};
		border-radius: 4px;
		padding: 10px;

		:hover {
			color: ${colors.hoverText};

			.chapterMenu {
				display: flex;
			}
		}

		.chapterName {
			a {
				color: ${colors.mainText};
			}
			a: hover {
				cursor: pointer;
				text-decoration: underline;
			}

			.chapterNumber {
				margin-right: 10px;

				.form-check-input {
					margin-top: 0;
				}
			}
		}

		svg {
			:hover {
				cursor: pointer;
			}
		}

		.chapterMenu {
			position: absolute;
			left: -32px;
			top: 2px;
			display: none;
			padding: 0 15px 10px 0;

			svg:hover {
				background-color: ${colors.background};
				cursor: pointer;
			}
		}
	}

	.pageGroupsContainer {
		font-size: 14px;
	}
`;

export const pageGroupContainer = css`
	margin-bottom: 10px;
	cursor: grab;

	.pageGroupHeader {
		font-weight: 600;
		margin-bottom: 10px;
		position: relative;

		:hover {
			color: ${colors.hoverText};

			.pageGroupMenu {
				display: flex;
			}
		}

		.pageGroupMenu {
			position: absolute;
			left: -40px;
			top: 3px;
			padding: 0 10px;
			display: none;

			svg:hover {
				background-color: ${colors.background};
				cursor: pointer;
			}
		}

		a {
			color: ${colors.mainText};
		}

		a: hover {
			cursor: pointer;
			text-decoration: underline;
		}

		svg: hover {
			cursor: pointer;
		}
	}

	.pageGroupBody {
		padding-bottom: 2px;
	}
`;

export const pageBlock = css`
	:hover {
		.pageContainer {
			.pageMenu {
				display: flex;
			}
		}
	}
	:not(:last-child) {
		.pageContainer {
			border-bottom: 1px solid ${colors.border};
		}
	}

	.pageContainer {
		cursor: grab;
		position: relative;
		margin-left: 30px;
		padding: 3px 0;

		.pageMenu {
			position: absolute;
			left: -40px;
			top: 3px;
			padding: 0 10px;
			display: none;
			color: ${colors.mainText};

			svg:hover {
				cursor: pointer;
				background-color: ${colors.background};
			}
		}

		.notNormalPage {
			font-weight: bold;
			font-style: italic;
		}

		a {
			color: ${colors.mainText};
		}

		a:hover {
			color: ${colors.hoverText};
			cursor: pointer;
			text-decoration: underline;
		}

		:hover {
			${boxShadowMixin(1)};
		}
	}
`;
